import { useLabelVersion } from '../../features/labels/LabelsProvider';
import { labelId, labelRegion } from '../../features/labels/labels';
import React, { useEffect, useMemo } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import GridViewIcon from '@mui/icons-material/GridView';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Mapea claves de región a nombres legibles
const regionNames = {
  peru: 'Perú',
  fonchi: 'Fonchi',
  aysen: 'Aysén',
  loslagos: 'Los Lagos',
};

// Formatea la etiqueta de un área (e.g., 'puertomontt' -> 'Puerto Montt')
function formatArea(selArea) {
  const raw = selArea.includes('-') ? selArea.split('-')[1] : selArea;
  const lower = raw.toLowerCase();

  if (lower.startsWith('puerto')) {
    const suffix = lower.slice('puerto'.length);
    return 'Puerto ' + suffix.charAt(0).toUpperCase() + suffix.slice(1);
  }

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Extrae y capitaliza el nombre de un centro a partir de su clave o raw
function formatCenterName(key, raw) {
  const name = key ? key.split('-')[0] : raw.split('-')[0];
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

const PanelAreas = ({
  Regions,
  capitalize,
  servicioFiltrado,
  handleServiceClick,
  serviciosCentro,
  selRegion,
  setSelRegion,
  selArea,
  setSelArea,
  estados,
}) => {
  useLabelVersion();
  const visibleAreasByRegion = useMemo(() => {
    const groupedAreas = {};

    Object.keys(estados.areas || {}).forEach(areaKey => {
      const matchingServiceAreaKey = Object.keys(serviciosCentro || {})
        .find(key => key.toLowerCase() === areaKey.toLowerCase());
      const centerServicesMap = matchingServiceAreaKey
        ? serviciosCentro[matchingServiceAreaKey]
        : {};

      const hasVisibleCenters = Object.values(centerServicesMap || {}).some(centerServices => {
        if (servicioFiltrado) {
          return centerServices?.[servicioFiltrado]?.status_service === true;
        }

        return Object.values(centerServices || {}).some(
          serviceInfo => serviceInfo?.status_service === true
        );
      });

      if (!hasVisibleCenters) return;

      const [region] = areaKey.split("-");
      if (!groupedAreas[region]) groupedAreas[region] = [];
      groupedAreas[region].push(areaKey);
    });

    return groupedAreas;
  }, [estados.areas, serviciosCentro, servicioFiltrado]);

  const visibleRegions = useMemo(
    () => Regions.filter(region => (visibleAreasByRegion[region] || []).length > 0),
    [Regions, visibleAreasByRegion]
  );

  // Mapa de servicios para el área seleccionada (case-insensitive)
  const serviciosMap = useMemo(() => {
    const key = Object.keys(serviciosCentro)
      .find(k => k.toLowerCase() === selArea?.toLowerCase());
    return key ? serviciosCentro[key] : {};
  }, [serviciosCentro, selArea]);

  // Áreas de la región seleccionada
  const areasForRegion = useMemo(
    () => visibleAreasByRegion[selRegion] || [],
    [visibleAreasByRegion, selRegion]
  );

  // Centros del área seleccionada
  const centersForArea = useMemo(() => {
    const uniqueCenters = new Map();

    Object.keys(serviciosMap || {}).forEach(centerKey => {
      const normalizedKey = centerKey.split('-')[0].toLowerCase();
      if (!uniqueCenters.has(normalizedKey)) {
        uniqueCenters.set(normalizedKey, centerKey);
      }
    });

    return Array.from(uniqueCenters.values());
  }, [serviciosMap]);


  // Filtra los centros que tengan al menos un servicio activo
  const filteredCenters = useMemo(() => {
    return centersForArea.filter(centerKey => {
      // Normalizamos claves
      const matchedKey = Object.keys(serviciosMap)
        .find(k => k.toLowerCase() === centerKey.toLowerCase());

      if (!matchedKey) return false; // No existe el centro en servicios

      const serviciosCentroActual = serviciosMap[matchedKey];
      if (!serviciosCentroActual) return false;

      // Si hay servicio seleccionado, filtramos SOLO ese
      if (servicioFiltrado) {
        const servicio = serviciosCentroActual[servicioFiltrado];
        return servicio?.status_service === true;
      }

      // SIN servicio seleccionado → mostrar solo centros con algún servicio activo
      return Object.values(serviciosCentroActual).some(
        s => s?.status_service === true
      );
    });
  }, [centersForArea, serviciosMap, servicioFiltrado]);



  const isFlux = servicioFiltrado === 'flux';

  useEffect(() => {
    if (!visibleRegions.length) {
      if (selRegion !== null) setSelRegion(null);
      if (selArea !== null) setSelArea(null);
      return;
    }

    if (!selRegion || !visibleRegions.includes(selRegion)) {
      const nextRegion = visibleRegions[0];
      setSelRegion(nextRegion);
      setSelArea(visibleAreasByRegion[nextRegion]?.[0] || null);
      return;
    }

    if (!selArea || !areasForRegion.includes(selArea)) {
      setSelArea(areasForRegion[0] || null);
    }
  }, [areasForRegion, selArea, selRegion, setSelArea, setSelRegion, visibleAreasByRegion, visibleRegions]);

  useEffect(() => {
    if (servicioFiltrado === 'flux') {
      setSelRegion('flux');
      setSelArea('flux-barcos');
    }
  }, [servicioFiltrado, setSelRegion, setSelArea]);




  return (
    <div className="panel">

      {/* SECCIÓN REGIONES */}
      { !isFlux && 
        <section className="areas-section">
        <h2 className="panel-title">
          <LocationOnIcon className="panel-title-icon" />
          Regiones disponibles
        </h2>
        <div className="button-list">
          {visibleRegions.map(r => {
            const isSelected = r === selRegion;
            return (
              <button
                key={r}
                className={isSelected ? 'region-btn selected' : 'region-btn'}
                style={{padding:'0 1vw'}}
                onClick={() => {
                  setSelRegion(r);
                  setSelArea(null); // Reiniciar área al cambiar región
                }}
              >
                <LocationOnIcon className="panel-button-icon" />
                <span>{labelRegion(r, capitalize(regionNames[r]) || capitalize(r))}</span>
              </button>
            );
          })}
        </div>
        </section>
      }
      {/* SECCIÓN ÁREAS */}
      { !isFlux &&
        <section className="areas-section">
          <h2 className="panel-title">
            <GridViewIcon className="panel-title-icon" />
            Áreas de {labelRegion(selRegion, capitalize(regionNames[selRegion]) || capitalize(selRegion))}
          </h2>
          <div className="button-list">
            {areasForRegion.map(a => {
              const [, name] = a.split('-');
              const isSelected = a === selArea;
              return (
                // <li key={a}>
                  <button
                    className={isSelected ? 'area-btn selected' : 'area-btn'}
                    style={{padding:'0 1vw'}}
                    onClick={() => setSelArea(a)}
                  >
                    <LocationOnIcon className="panel-button-icon" />
                    <span>{labelId(a, 'ra', capitalize(name))}</span>
                  </button>
                // </li>
              );
            })}
          </div>
        </section>
      }

      {/* SECCIÓN CENTROS */}
      {selArea && (
        <section className="centers-section">
          <h2 className="panel-title" style={{marginBottom:isFlux && '2vh'}}>
            <BusinessIcon className="panel-title-icon" />
            {/* Centros de {formatArea(selArea)} */}
             {isFlux ? 'Barcos' : `Centros de ${labelId(selArea, 'ra', formatArea(selArea))}`}
          </h2>
          <ul className="button-list">
            {filteredCenters.map(c => {
              const matchedKey = Object.keys(serviciosMap)
                .find(k => k.toLowerCase() === c.toLowerCase());
              const svcInfo = serviciosMap[matchedKey]?.[servicioFiltrado];
              const [centro, area, region] = c.split('-')

              // console.log('c',centro)
              return (
                // <li key={c} className="center-item">
                  <button
                    key={c}
                    className="center-header"
                    // className={isSelected ? 'area-btn selected' : 'area-btn'}
                    onClick={e => {
                      e.stopPropagation();
                      localStorage.setItem('REGION',region)
                      localStorage.setItem('AREA',area)
                      localStorage.setItem('CENTRO',centro)
                      handleServiceClick(servicioFiltrado, svcInfo, c);
                    }}
                  >
                    <BusinessIcon className="panel-button-icon" />
                    <span>{labelId(c, 'car', capitalize(formatCenterName(matchedKey, c)))}</span>
                  </button>
                // </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
};

export default PanelAreas;
